/**
 * Theme-side Consentmo gate for Hotjar, Attentive, and Snapchat.
 *
 * Consentmo is the CMP (not Cookiebot). These three vendors load outside
 * Consentmo auto-blocking:
 *   - Hotjar was an ungated theme embed (now injected only after analytics)
 *   - Attentive dtag.js is a deferred app embed + Shopify asyncLoad URL
 *   - Snapchat shopify-scevent-init.js is a Shopify asyncLoad URL (not a WPM pixel)
 *
 * Fail closed until Consentmo is available and the visitor has made an
 * explicit choice. First visit (null / undefined status) stays denied even
 * if analyticsAllowed() / marketingAllowed() briefly return true — common
 * in non-banner geos before any accept/decline is stored.
 */
(function (window, document) {
  "use strict";

  if (window.GATConsent) return;

  var HOTJAR_ID = 3478103;
  var HOTJAR_SV = 6;
  var HOTJAR_SRC = "https://static.hotjar.com/c/hotjar-" + HOTJAR_ID + ".js?sv=" + HOTJAR_SV;
  var ATTENTIVE_SRC = "https://cdn.attn.tv/gatsport/dtag.js";
  var SNAPCHAT_SRC =
    "https://intg.snapchat.com/shopify/shopify-scevent-init.js?id=fc9b7be6-dc64-4025-9d19-44631305b6bc";

  var VENDORS = {
    hotjar: {
      id: "gat-consent-hotjar",
      src: HOTJAR_SRC,
      category: "analytics",
      match: /static\.hotjar\.com|hotjar-\d+/i,
      cookies: ["_hj"],
    },
    attentive: {
      id: "gat-consent-attentive",
      src: ATTENTIVE_SRC,
      category: "marketing",
      match: /cdn\.attn\.tv|attentivemobile|\.attn\.|\/dtag\.js|\/S5P\.js|unified-tag\.js/i,
      cookies: ["__attentive", "_attn_"],
    },
    snapchat: {
      id: "gat-consent-snapchat",
      src: SNAPCHAT_SRC,
      category: "marketing",
      match: /intg\.snapchat\.com|sc-static\.net|tr\.snapchat\.com|shopify-scevent-init|scevent/i,
      cookies: ["_scid", "_scid_r"],
      analyticsCookies: ["_scida"],
    },
  };

  var loaded = {};
  var lastAllowed = { analytics: false, marketing: false, preferences: false };
  var consentmoBound = false;
  var loadEventFired = document.readyState === "complete";

  function getConsentmo() {
    return window.Consentmo || null;
  }

  function normalizeStatus(value) {
    if (value == null || value === "") return null;
    if (typeof value === "object") {
      if (value.status != null) return String(value.status);
      if (value.state != null) return String(value.state);
      return null;
    }
    return String(value);
  }

  function getStatus() {
    var C = getConsentmo();
    if (!C) return null;
    try {
      if (typeof C.getStatus === "function") return normalizeStatus(C.getStatus());
      if (C.status != null) return normalizeStatus(C.status);
    } catch (e) {}
    return null;
  }

  // Consentmo uses the string "undefined" when no choice has been made yet.
  // JS null/undefined from a missing getter is the same first-visit state.
  function hasExplicitConsentChoice(status) {
    return !!(status && status !== "undefined" && status !== "null");
  }

  function isDeclineStatus(status) {
    return status === "decline" || status === "decline_all";
  }

  function isAcceptLikeStatus(status) {
    return (
      status === "accept_all" ||
      status === "allow" ||
      status === "accept" ||
      status === "accepted" ||
      status === "custom" ||
      status === "do_not_sell"
    );
  }

  function readConsentObject() {
    var C = getConsentmo();
    if (!C || typeof C.currentVisitorConsent !== "function") return null;
    try {
      return C.currentVisitorConsent() || null;
    } catch (e) {
      return null;
    }
  }

  function categoryAllowed(name) {
    var C = getConsentmo();
    if (!C) return false;

    var status = getStatus();
    // Independent of Allowed(): no stored choice is always deny for these vendors.
    if (!hasExplicitConsentChoice(status) || isDeclineStatus(status) || !isAcceptLikeStatus(status)) {
      return false;
    }

    try {
      var method = name + "Allowed";
      if (typeof C[method] === "function") return !!C[method]();
    } catch (e) {}

    var consent = readConsentObject();
    if (consent && typeof consent[name] === "boolean") return consent[name];

    return status === "accept_all" || status === "allow" || status === "accept" || status === "accepted";
  }

  function analyticsAllowed() {
    return categoryAllowed("analytics");
  }

  function marketingAllowed() {
    return categoryAllowed("marketing");
  }

  function preferencesAllowed() {
    return categoryAllowed("preferences");
  }

  function classify(url) {
    if (!url) return null;
    var key;
    for (key in VENDORS) {
      if (Object.prototype.hasOwnProperty.call(VENDORS, key) && VENDORS[key].match.test(url)) {
        return key;
      }
    }
    return null;
  }

  function vendorAllowed(name) {
    var vendor = VENDORS[name];
    if (!vendor) return false;
    return categoryAllowed(vendor.category);
  }

  function isManaged(el) {
    return !!(el && el.getAttribute && el.getAttribute("data-gat-consent-allowed") === "1");
  }

  function shouldBlockUrl(url, el) {
    var name = classify(url);
    if (!name) return false;
    if (vendorAllowed(name)) return false;
    if (el && isManaged(el)) return false;
    return true;
  }

  function neutralizeScript(el, url) {
    if (!el) return;
    var blockedSrc = url || el.getAttribute("data-gat-blocked-src") || el.getAttribute("src") || "";
    if (blockedSrc) el.setAttribute("data-gat-blocked-src", blockedSrc);
    el.setAttribute("type", "text/plain");
    el.setAttribute("data-gat-consent-blocked", "1");
    try {
      el.removeAttribute("src");
    } catch (e) {}
  }

  function deleteCookies(prefixes) {
    if (!prefixes || !prefixes.length) return;
    var raw = document.cookie ? document.cookie.split(";") : [];
    var host = window.location.hostname.replace(/^www\./, "");
    var domains = ["", window.location.hostname, "." + window.location.hostname, host, "." + host];
    var paths = ["/", window.location.pathname];
    var i;
    var j;
    var k;
    var name;

    for (i = 0; i < raw.length; i++) {
      name = raw[i].split("=")[0].replace(/^\s+|\s+$/g, "");
      if (!name) continue;
      for (j = 0; j < prefixes.length; j++) {
        if (name === prefixes[j] || name.indexOf(prefixes[j]) === 0) {
          for (k = 0; k < domains.length; k++) {
            var domainPart = domains[k] ? "; domain=" + domains[k] : "";
            var p;
            for (p = 0; p < paths.length; p++) {
              document.cookie =
                name +
                "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=" +
                paths[p] +
                domainPart +
                "; SameSite=Lax";
            }
          }
          break;
        }
      }
    }
  }

  function removeVendorNodes(name) {
    var vendor = VENDORS[name];
    if (!vendor) return;
    var nodes = document.querySelectorAll("script[src], script[data-gat-blocked-src], iframe[src]");
    var i;
    for (i = 0; i < nodes.length; i++) {
      var src = nodes[i].getAttribute("src") || nodes[i].getAttribute("data-gat-blocked-src") || "";
      if (vendor.match.test(src)) {
        if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
      }
    }
    loaded[name] = false;
  }

  function injectVendor(name) {
    var vendor = VENDORS[name];
    if (!vendor || loaded[name] || !vendorAllowed(name)) return;
    if (document.getElementById(vendor.id)) {
      loaded[name] = true;
      return;
    }

    var existing = document.querySelectorAll("script[src]");
    var i;
    for (i = 0; i < existing.length; i++) {
      if (vendor.match.test(existing[i].src || "")) {
        loaded[name] = true;
        return;
      }
    }

    if (name === "hotjar") {
      window.hj =
        window.hj ||
        function () {
          (window.hj.q = window.hj.q || []).push(arguments);
        };
      window._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_SV };
    }

    // Shopify asyncLoad inserts Attentive + Snapchat on window.load.
    // If consent is already granted before that, let Shopify inject once.
    if ((name === "attentive" || name === "snapchat") && !loadEventFired) {
      return;
    }

    var script = document.createElement("script");
    script.id = vendor.id;
    script.async = true;
    script.setAttribute("data-gat-consent-allowed", "1");
    script.src = vendor.src;
    document.head.appendChild(script);
    loaded[name] = true;
  }

  function applyConsent() {
    var next = {
      analytics: analyticsAllowed(),
      marketing: marketingAllowed(),
      preferences: preferencesAllowed(),
    };

    if (next.analytics) {
      injectVendor("hotjar");
    } else {
      removeVendorNodes("hotjar");
      deleteCookies(VENDORS.hotjar.cookies);
      try {
        delete window.hj;
        delete window._hjSettings;
      } catch (e) {
        window.hj = undefined;
        window._hjSettings = undefined;
      }
    }

    if (next.marketing) {
      injectVendor("attentive");
      injectVendor("snapchat");
    } else {
      removeVendorNodes("attentive");
      removeVendorNodes("snapchat");
      deleteCookies(VENDORS.attentive.cookies);
      deleteCookies(VENDORS.snapchat.cookies);
    }

    if (!next.analytics) {
      deleteCookies(VENDORS.snapchat.analyticsCookies);
    }

    lastAllowed = next;
    syncShopify();
  }

  function syncShopify() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.setTrackingConsent !== "function") return;
    if (!getConsentmo()) return;

    var status = getStatus();
    if (!hasExplicitConsentChoice(status)) return;

    privacy.setTrackingConsent(
      {
        analytics: lastAllowed.analytics,
        marketing: lastAllowed.marketing,
        preferences: lastAllowed.preferences,
        sale_of_data: lastAllowed.marketing,
      },
      function () {}
    );
  }

  function bindConsentmo() {
    if (consentmoBound) {
      applyConsent();
      return;
    }
    var C = getConsentmo();
    if (!C) return;
    consentmoBound = true;

    var eventName = (C.events && C.events.ConsentUpdate) || "ConsentUpdate";
    document.addEventListener(eventName, applyConsent);
    document.addEventListener("visitorConsentCollected", applyConsent);
    applyConsent();
  }

  function waitForConsentmo() {
    bindConsentmo();
    if (consentmoBound) return;

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      bindConsentmo();
      if (consentmoBound || attempts > 200) window.clearInterval(timer);
    }, 50);
  }

  function inspectNode(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.tagName !== "SCRIPT" && node.tagName !== "IFRAME" && node.tagName !== "IMG") return;
    var url = node.getAttribute("src") || node.getAttribute("data-src") || "";
    if (shouldBlockUrl(url, node)) neutralizeScript(node, url);
  }

  function inspectTree(root) {
    if (!root || root.nodeType !== 1) return;
    inspectNode(root);
    var nodes = root.querySelectorAll("script, iframe, img");
    var i;
    for (i = 0; i < nodes.length; i++) inspectNode(nodes[i]);
  }

  function patchScriptLoading() {
    var originalCreateElement = document.createElement;
    document.createElement = function (tagName) {
      var el = originalCreateElement.apply(document, arguments);
      if (String(tagName).toLowerCase() === "script") {
        markPatched(el);
      }
      return el;
    };

    var proto = window.HTMLScriptElement && window.HTMLScriptElement.prototype;
    if (!proto) return;

    var srcDesc = Object.getOwnPropertyDescriptor(proto, "src");
    if (srcDesc && srcDesc.set && srcDesc.get) {
      Object.defineProperty(proto, "src", {
        configurable: true,
        enumerable: true,
        get: function () {
          return srcDesc.get.call(this);
        },
        set: function (value) {
          if (shouldBlockUrl(value, this)) {
            neutralizeScript(this, value);
            return;
          }
          srcDesc.set.call(this, value);
        },
      });
    }

    var originalSetAttribute = proto.setAttribute;
    proto.setAttribute = function (name, value) {
      if (String(name).toLowerCase() === "src" && shouldBlockUrl(value, this)) {
        neutralizeScript(this, value);
        return;
      }
      return originalSetAttribute.apply(this, arguments);
    };
  }

  function markPatched(el) {
    el.setAttribute("data-gat-consent-patched", "1");
  }

  function startObserver() {
    if (!window.MutationObserver) {
      inspectTree(document.documentElement);
      return;
    }
    var observer = new MutationObserver(function (mutations) {
      var i;
      var j;
      for (i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (j = 0; j < added.length; j++) inspectTree(added[j]);
      }
    });
    observer.observe(document.documentElement || document, { childList: true, subtree: true });
    if (document.documentElement) inspectTree(document.documentElement);
  }

  window.GATConsent = {
    analyticsAllowed: analyticsAllowed,
    marketingAllowed: marketingAllowed,
    preferencesAllowed: preferencesAllowed,
    getStatus: getStatus,
    classify: classify,
    shouldBlockUrl: shouldBlockUrl,
    refresh: applyConsent,
    vendors: VENDORS,
  };

  patchScriptLoading();
  startObserver();
  waitForConsentmo();

  window.addEventListener("load", function () {
    loadEventFired = true;
    // Shopify asyncLoad also binds to window.load. Give it a turn to insert
    // Attentive/Snapchat before we inject our own copies.
    window.setTimeout(applyConsent, 50);
  });

  window.Shopify = window.Shopify || {};
  if (typeof window.Shopify.loadFeatures === "function") {
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) return;
        syncShopify();
      }
    );
  }
})(window, document);
